import { FunctionComponent } from 'react';
import { IconChevronRight } from '@tock/icons';
import { NavLink, useRouteMatch } from 'react-router-dom';
import { Typography } from 'omakase';
import { core } from '@tock/coretypes';
import { getFullName, requiresEmailConfirmation } from '@tock/shared/utils/patron';
import { guestImageUrl } from '@tock/images';
import { ifClickable } from '@tock/shared/utils/touchUtils';
import { noop } from 'es-toolkit/compat';
import Anchor from 'components/Anchor';

interface Props {
  patronProfile: core.PatronProfile;
  patron?: core.Patron;
}

const AccountNav: FunctionComponent<Props> = ({ patron, patronProfile }) => {
  const match = useRouteMatch();
  const imageLink = patronProfile.patron?.imageUrl;
  const imageStyle = {
    backgroundImage: `url(${guestImageUrl(imageLink ?? undefined, 400)})`,
  };

  return (
    <>
      <nav aria-label="account-nav" className="Account-mobileNav">
        <div className="Account-profileHeading">
          <div className="Account-imageContainer">
            <div className="Account-image" style={imageStyle} />
          </div>
          <div className="Account-name">
            <Typography variant="heading02">{getFullName(patron)}</Typography>
            <div className="Account-profileLink">
              <Typography component="span" variant="body02Strong">
                <Anchor href="/profile">View my profile</Anchor>
              </Typography>
            </div>
          </div>
        </div>
        <div className="Account-navItem">
          <NavLink
            exact
            activeClassName="is-active"
            className="Account-navLink"
            to={`${match.path}/profile`}
          >
            <div className="Account-navArrow">
              <IconChevronRight />
            </div>
            <Typography color="inherit" component="span" variant="heading03">
              Account
            </Typography>
          </NavLink>
        </div>

        {patron &&
          !requiresEmailConfirmation(patron) && [
            <div key={2} className="Account-navItem">
              <NavLink
                activeClassName="is-active"
                className="Account-navLink"
                // disabled={requiresEmailConfirmation(patron)}
                to={`${match.path}/transactions`}
                onClick={ifClickable(noop)}
              >
                <div className="Account-navArrow">
                  <IconChevronRight />
                </div>
                <Typography color="inherit" component="span" variant="heading03">
                  Transactions
                </Typography>
              </NavLink>
            </div>,
          ]}

        <div className="Account-navItem">
          <NavLink
            exact
            activeClassName="is-active"
            className="Account-navLink"
            to={`${match.path}/playlists`}
          >
            <div className="Account-navArrow">
              <IconChevronRight />
            </div>
            <Typography color="inherit" component="span" variant="heading03">
              Saved
            </Typography>
          </NavLink>
        </div>
        <div className="Account-navItem">
          <NavLink
            activeClassName="is-active"
            className="Account-navLink"
            to={`${match.path}/gift-cards`}
          >
            <div className="Account-navArrow">
              <IconChevronRight />
            </div>
            <Typography color="inherit" component="span" variant="heading03">
              Gift cards
            </Typography>
          </NavLink>
        </div>

        <div className="Account-navItem">
          <NavLink
            activeClassName="is-active"
            className="Account-navLink"
            to={`${match.path}/billing`}
          >
            <div className="Account-navArrow">
              <IconChevronRight />
            </div>
            <Typography color="inherit" component="span" variant="heading03">
              Payment methods
            </Typography>
          </NavLink>
        </div>
      </nav>
      <nav className="Account-nav">
        <div className="Account-navItem">
          <NavLink
            exact
            activeClassName="is-active"
            className="Account-navLink"
            to={`${match.path}/profile`}
            data-testid="account-nav-profile"
          >
            <Typography color="inherit" component="span" variant="body02">
              Account
            </Typography>
          </NavLink>
        </div>

        {/* TODO: RR4 removed disabled prop, revert to old behavior */}
        {patron &&
          !requiresEmailConfirmation(patron) && [
            <div key={2} className="Account-navItem">
              <NavLink
                activeClassName="is-active"
                className="Account-navLink"
                // disabled={requiresEmailConfirmation(patron)}
                to={`${match.path}/transactions`}
                onClick={ifClickable(noop)}
                data-testid="account-nav-transactions"
              >
                <Typography color="inherit" component="span" variant="body02">
                  Transactions
                </Typography>
              </NavLink>
            </div>,
          ]}

        <div className="Account-navItem">
          <NavLink
            activeClassName="is-active"
            className="Account-navLink"
            to={`${match.path}/gift-cards`}
            data-testid="account-nav-gift-cards"
          >
            <Typography color="inherit" component="span" variant="body02">
              Gift cards
            </Typography>
          </NavLink>
        </div>

        <div className="Account-navItem">
          <NavLink
            activeClassName="is-active"
            className="Account-navLink"
            to={`${match.path}/billing`}
            data-testid="account-nav-payment-methods"
          >
            <Typography color="inherit" component="span" variant="body02">
              Payment methods
            </Typography>
          </NavLink>
        </div>
      </nav>
    </>
  );
};

export default AccountNav;
